import { TokenManager } from "@/supabase/supabaseClient";
import type {
  Tenant,
  TenantSettings,
  TenantMember,
  CreateTenantData,
  UpdateTenantSettingsData,
  PlatformStats,
  PlatformUser,
  Plan,
  CreatePlanData,
} from "@/types/tenant";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

async function getAuthHeaders(
  tenantId?: string
): Promise<Record<string, string>> {
  const tokenReady = await TokenManager.ensureTokenReady();
  if (!tokenReady) throw new Error("Authentication token not available");

  const token = TokenManager.getStoredToken();
  if (!token) throw new Error("No token available");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  if (tenantId) {
    headers["X-Tenant-ID"] = tenantId;
  }

  return headers;
}

export class TenantService {
  // ============================================================
  // Public: Tenant Resolution
  // ============================================================

  /**
   * Resolve a tenant from a slug (e.g., from subdomain).
   * No auth required.
   */
  static async resolveTenantBySlug(slug: string): Promise<Tenant | null> {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/resolve?slug=${encodeURIComponent(slug)}`,
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) return null;

      const result = await response.json();
      return result.tenant || null;
    } catch (error) {
      console.error("Error resolving tenant:", error);
      return null;
    }
  }

  /**
   * Resolve a tenant from a custom domain.
   * No auth required.
   */
  static async resolveTenantByDomain(domain: string): Promise<Tenant | null> {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/resolve?domain=${encodeURIComponent(domain)}`,
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response.ok) return null;

      const result = await response.json();
      return result.tenant || null;
    } catch (error) {
      console.error("Error resolving tenant by domain:", error);
      return null;
    }
  }

  // ============================================================
  // Tenant Settings (Tenant Admin)
  // ============================================================

  static async getSettings(tenantId: string): Promise<TenantSettings | null> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(`${apiBaseUrl}/api/tenant/settings`, {
        headers,
      });

      if (!response.ok) return null;

      const result = await response.json();
      return result.settings || null;
    } catch (error) {
      console.error("Error fetching tenant settings:", error);
      return null;
    }
  }

  static async updateSettings(
    tenantId: string,
    settings: UpdateTenantSettingsData
  ): Promise<TenantSettings | null> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(`${apiBaseUrl}/api/tenant/settings`, {
        method: "PUT",
        headers,
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update settings");
      }

      const result = await response.json();
      return result.settings;
    } catch (error) {
      console.error("Error updating tenant settings:", error);
      throw error;
    }
  }

  // ============================================================
  // Tenant Members
  // ============================================================

  static async getMembers(tenantId: string): Promise<TenantMember[]> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(`${apiBaseUrl}/api/tenant/members`, {
        headers,
      });

      if (!response.ok) return [];

      const result = await response.json();
      return result.members || [];
    } catch (error) {
      console.error("Error fetching tenant members:", error);
      return [];
    }
  }

  static async addMember(
    tenantId: string,
    userId: string,
    role: string = "member"
  ): Promise<TenantMember | null> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(`${apiBaseUrl}/api/tenant/members`, {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId, role }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to add member");
      }

      const result = await response.json();
      return result.member;
    } catch (error) {
      console.error("Error adding tenant member:", error);
      throw error;
    }
  }

  static async updateMemberRole(
    tenantId: string,
    memberId: string,
    role: string
  ): Promise<TenantMember | null> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/members/${memberId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ role }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update member role");
      }

      const result = await response.json();
      return result.member;
    } catch (error) {
      console.error("Error updating tenant member:", error);
      throw error;
    }
  }

  static async removeMember(
    tenantId: string,
    memberId: string
  ): Promise<boolean> {
    try {
      const headers = await getAuthHeaders(tenantId);
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/members/${memberId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error removing tenant member:", error);
      return false;
    }
  }

  // ============================================================
  // Super Admin: Platform Management
  // ============================================================

  static async listAllTenants(): Promise<Tenant[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/api/tenant/admin/list`, {
        headers,
      });

      if (!response.ok) return [];

      const result = await response.json();
      return result.tenants || [];
    } catch (error) {
      console.error("Error listing tenants:", error);
      return [];
    }
  }

  static async checkSlugAvailability(
    slug: string,
    excludeTenantId?: string
  ): Promise<{ available: boolean; reason?: string }> {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ slug });
      if (excludeTenantId) params.set("excludeTenantId", excludeTenantId);

      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/check-slug?${params.toString()}`,
        { headers }
      );

      return await response.json();
    } catch {
      return { available: false, reason: "Check failed" };
    }
  }

  static async createTenant(data: CreateTenantData): Promise<Tenant | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/api/tenant/admin/create`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create tenant");
      }

      const result = await response.json();
      return result.tenant;
    } catch (error) {
      console.error("Error creating tenant:", error);
      throw error;
    }
  }

  static async updateTenant(
    tenantId: string,
    data: Partial<Tenant>
  ): Promise<Tenant | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update tenant");
      }

      const result = await response.json();
      return result.tenant;
    } catch (error) {
      console.error("Error updating tenant:", error);
      throw error;
    }
  }

  static async deleteTenant(tenantId: string): Promise<boolean> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error deleting tenant:", error);
      return false;
    }
  }

  static async getPlatformStats(): Promise<PlatformStats | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/api/tenant/admin/stats`, {
        headers,
      });

      if (!response.ok) return null;

      const result = await response.json();
      return result.stats;
    } catch (error) {
      console.error("Error fetching platform stats:", error);
      return null;
    }
  }

  static async updateFeatureFlags(
    tenantId: string,
    features: Record<string, unknown>
  ): Promise<TenantSettings | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/features`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(features),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update features");
      }

      const result = await response.json();
      return result.settings;
    } catch (error) {
      console.error("Error updating feature flags:", error);
      throw error;
    }
  }

  // ============================================================
  // Super Admin: User Management
  // ============================================================

  static async listPlatformUsers(
    search?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    users: PlatformUser[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("page", page.toString());
      params.set("limit", limit.toString());

      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/users?${params.toString()}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      return await response.json();
    } catch (error) {
      console.error("Error listing platform users:", error);
      throw error;
    }
  }

  static async assignPlatformRole(
    userId: string,
    roleName: string
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/users/${userId}/platform-roles`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ role_name: roleName }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to assign role");
      }
    } catch (error) {
      console.error("Error assigning platform role:", error);
      throw error;
    }
  }

  static async removePlatformRole(
    userId: string,
    roleName: string
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/users/${userId}/platform-roles/${roleName}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove role");
      }
    } catch (error) {
      console.error("Error removing platform role:", error);
      throw error;
    }
  }

  static async getUserTenants(
    userId: string
  ): Promise<
    {
      id: string;
      role: string;
      joined_at: string;
      tenant: { id: string; name: string; slug: string; is_active: boolean };
    }[]
  > {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/users/${userId}/tenants`,
        { headers }
      );

      if (!response.ok) return [];

      const result = await response.json();
      return result.memberships || [];
    } catch (error) {
      console.error("Error fetching user tenants:", error);
      return [];
    }
  }

  // ============================================================
  // Super Admin: Plans Management
  // ============================================================

  static async listPlans(): Promise<Plan[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/api/tenant/admin/plans`, {
        headers,
      });

      if (!response.ok) return [];

      const result = await response.json();
      return result.plans || [];
    } catch (error) {
      console.error("Error listing plans:", error);
      return [];
    }
  }

  static async createPlan(data: CreatePlanData): Promise<Plan | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiBaseUrl}/api/tenant/admin/plans`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create plan");
      }

      const result = await response.json();
      return result.plan;
    } catch (error) {
      console.error("Error creating plan:", error);
      throw error;
    }
  }

  static async updatePlan(
    planId: string,
    data: Partial<Plan>
  ): Promise<Plan | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/plans/${planId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update plan");
      }

      const result = await response.json();
      return result.plan;
    } catch (error) {
      console.error("Error updating plan:", error);
      throw error;
    }
  }

  static async deletePlan(planId: string): Promise<boolean> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/plans/${planId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete plan");
      }

      return true;
    } catch (error) {
      console.error("Error deleting plan:", error);
      throw error;
    }
  }

  // ============================================================
  // Super Admin: Tenant Settings (Platform Admin)
  // ============================================================

  static async getTenantSettingsAsAdmin(
    tenantId: string
  ): Promise<TenantSettings | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/settings`,
        { headers }
      );

      if (!response.ok) return null;

      const result = await response.json();
      return result.settings || null;
    } catch (error) {
      console.error("Error fetching tenant settings as admin:", error);
      return null;
    }
  }

  static async updateTenantSettingsAsAdmin(
    tenantId: string,
    settings: Partial<TenantSettings>
  ): Promise<TenantSettings | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/settings`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(settings),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update settings");
      }

      const result = await response.json();
      return result.settings;
    } catch (error) {
      console.error("Error updating tenant settings as admin:", error);
      throw error;
    }
  }

  // ============================================================
  // Super Admin: Dedicated Database Config
  // ============================================================

  static async getTenantDbConfig(
    tenantId: string
  ): Promise<TenantDbConfig | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/db-config`,
        { headers }
      );

      if (!response.ok) return null;

      const result = await response.json();
      return result.config || null;
    } catch (error) {
      console.error("Error fetching tenant DB config:", error);
      return null;
    }
  }

  static async saveTenantDbConfig(
    tenantId: string,
    config: {
      supabase_url: string;
      supabase_anon_key: string;
      supabase_service_role_key: string;
    }
  ): Promise<TenantDbConfig | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/db-config`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify(config),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save DB config");
      }

      const result = await response.json();
      return result.config;
    } catch (error) {
      console.error("Error saving tenant DB config:", error);
      throw error;
    }
  }

  static async testTenantDbConnection(
    tenantId: string
  ): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/test-db-connection`,
        { method: "POST", headers }
      );

      return await response.json();
    } catch (error) {
      console.error("Error testing DB connection:", error);
      return { success: false, error: "Network error" };
    }
  }

  static async provisionTenantDb(
    tenantId: string
  ): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/provision-db`,
        { method: "POST", headers }
      );

      return await response.json();
    } catch (error) {
      console.error("Error provisioning tenant DB:", error);
      return { success: false, error: "Network error" };
    }
  }

  static async migrateTenantToDedicatedDb(
    tenantId: string
  ): Promise<{
    success: boolean;
    tables?: TableMigrationResult[];
    error?: string;
  }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/migrate-to-dedicated`,
        { method: "POST", headers }
      );

      return await response.json();
    } catch (error) {
      console.error("Error migrating to dedicated DB:", error);
      return { success: false, error: "Network error" };
    }
  }

  static async migrateTenantToSharedDb(
    tenantId: string
  ): Promise<{
    success: boolean;
    tables?: TableMigrationResult[];
    error?: string;
  }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/migrate-to-shared`,
        { method: "POST", headers }
      );

      return await response.json();
    } catch (error) {
      console.error("Error migrating to shared DB:", error);
      return { success: false, error: "Network error" };
    }
  }

  static async hasDedicatedDb(tenantId: string): Promise<boolean> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${apiBaseUrl}/api/tenant/admin/${tenantId}/has-dedicated-db`,
        { headers }
      );

      if (!response.ok) return false;

      const result = await response.json();
      return result.hasDedicatedDb || false;
    } catch (error) {
      console.error("Error checking dedicated DB:", error);
      return false;
    }
  }
}

export interface TenantDbConfig {
  id: string;
  tenant_id: string;
  supabase_url: string;
  supabase_anon_key: string;
  supabase_service_role_key: string;
  is_provisioned: boolean;
  provisioned_at: string | null;
  migration_status: "pending" | "in_progress" | "completed" | "failed" | null;
  migrated_at: string | null;
  migration_error: string | null;
  migration_log: TableMigrationResult[] | null;
  created_at: string;
  updated_at: string;
}

export interface TableMigrationResult {
  table: string;
  status: "success" | "skipped" | "failed";
  rowCount: number;
  error?: string;
}
